import React from 'react';
import { Box, Typography, Paper, IconButton, Tooltip } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import './TimeGrid.css';

/**
 * TimeGrid - CSS Grid based weekly schedule (7 days x 24 hours)
 * Pure CSS Grid implementation with paint interaction and resize handles
 */
const TimeGrid = ({ slots, onMouseDown, onMouseEnter, onMouseUp, onResizeStart, onRemovePlaylist, onDeleteBtnHover }) => {
    const DAYS = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];
    const HOURS = Array.from({ length: 24 }, (_, i) => `${String(i).padStart(2, '0')}:00`);

    // Get slot data
    const getSlot = (day, hour) => {
        const key = `${day}-${hour}`;
        return slots[key] || null;
    };

    // Get contrast text color
    const getTextColor = (bgColor) => {
        if (!bgColor) return '#000';

        const hex = bgColor.replace('#', '');
        const r = parseInt(hex.substr(0, 2), 16);
        const g = parseInt(hex.substr(2, 2), 16);
        const b = parseInt(hex.substr(4, 2), 16);
        const brightness = (r * 299 + g * 587 + b * 114) / 1000;

        return brightness > 128 ? '#000' : '#fff';
    };

    return (
        <Paper
            elevation={3}
            sx={{
                bgcolor: 'white',
                border: '1px solid #e0e0e0',
                borderRadius: '12px',
                overflow: 'hidden'
            }}
            onMouseUp={onMouseUp}
            onMouseLeave={onMouseUp}
        >
            <div className="time-grid-container">
                {/* Empty corner cell */}
                <div className="time-grid-corner" />

                {/* Day headers */}
                {DAYS.map((day, index) => (
                    <div key={`day-${index}`} className="time-grid-day-header">
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                            {day}
                        </Typography>
                    </div>
                ))}

                {/* Hour labels and time slots */}
                {HOURS.map((hour, hourIndex) => (
                    <React.Fragment key={`row-${hourIndex}`}>
                        {/* Hour label */}
                        <div className="time-grid-hour-label">
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#495057' }}>
                                {hour}
                            </Typography>
                        </div>

                        {/* Time slots for each day */}
                        {DAYS.map((_, dayIndex) => {
                            const currentSlot = getSlot(dayIndex, hourIndex);
                            const slotAbove = getSlot(dayIndex, hourIndex - 1);
                            const slotBelow = getSlot(dayIndex, hourIndex + 1);

                            const bgColor = currentSlot?.color || '#f8f9fa';
                            const textColor = getTextColor(bgColor);

                            // Determine block position
                            const isSameAsAbove = currentSlot && slotAbove && currentSlot.playlistId === slotAbove.playlistId;
                            const isSameAsBelow = currentSlot && slotBelow && currentSlot.playlistId === slotBelow.playlistId;

                            const isBlockStart = currentSlot && !isSameAsAbove;
                            const isBlockEnd = currentSlot && !isSameAsBelow;
                            const isBlockMiddle = currentSlot && isSameAsAbove && isSameAsBelow;
                            const isBlockSingle = isBlockStart && isBlockEnd;

                            // Calculate block end hour for time range display
                            let blockEndHour = hourIndex;
                            if (isBlockStart) {
                                let checkHour = hourIndex;
                                while (checkHour < 23) {
                                    const nextSlot = getSlot(dayIndex, checkHour + 1);
                                    if (nextSlot && nextSlot.playlistId === currentSlot.playlistId) {
                                        checkHour++;
                                    } else {
                                        break;
                                    }
                                }
                                blockEndHour = checkHour + 1; // End hour is exclusive
                            }

                            // Build CSS classes
                            const classes = ['time-grid-slot'];
                            if (currentSlot) classes.push('has-playlist');
                            if (isBlockSingle) classes.push('block-single');
                            else if (isBlockStart) classes.push('block-start');
                            else if (isBlockEnd) classes.push('block-end');
                            else if (isBlockMiddle) classes.push('block-middle');

                            return (
                                <div
                                    key={`slot-${dayIndex}-${hourIndex}`}
                                    className={classes.join(' ')}
                                    style={{
                                        backgroundColor: bgColor,
                                        color: textColor
                                    }}
                                    onMouseDown={() => onMouseDown(dayIndex, hourIndex)}
                                    onMouseEnter={() => onMouseEnter(dayIndex, hourIndex)}
                                >
                                    {currentSlot && isBlockStart && (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', position: 'relative', zIndex: 1 }}>
                                            {/* שורה אחת: שם + שעות */}
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    fontSize: '6.5px',
                                                    fontWeight: 600,
                                                    lineHeight: 1.2,
                                                    textAlign: 'center',
                                                    width: '100%'
                                                }}
                                            >
                                                {currentSlot.name} • {`${String(hourIndex).padStart(2, '0')}:00-${String(blockEndHour).padStart(2, '0')}:00`}
                                            </Typography>

                                            {/* כפתור הסרת מערך תצוגה - קטן יותר */}
                                            {onRemovePlaylist && (
                                                <Tooltip title="הסר מערך תצוגה מטווח זה" placement="top">
                                                    <IconButton
                                                        className="remove-playlist-btn"
                                                        size="small"
                                                        onMouseEnter={() => onDeleteBtnHover && onDeleteBtnHover(true)}
                                                        onMouseLeave={() => onDeleteBtnHover && onDeleteBtnHover(false)}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onRemovePlaylist(dayIndex, hourIndex, blockEndHour, currentSlot.playlistId);
                                                        }}
                                                        sx={{
                                                            position: 'absolute',
                                                            top: 0,
                                                            left: 1,
                                                            bgcolor: 'rgba(255, 255, 255, 0.95)',
                                                            width: 14,
                                                            height: 14,
                                                            opacity: 0,
                                                            transition: 'all 0.2s ease',
                                                            boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                                                            cursor: 'pointer',
                                                            padding: 0,
                                                            minWidth: 0,
                                                            zIndex: 10,
                                                            '&:hover': {
                                                                bgcolor: '#fee',
                                                                transform: 'scale(1.15)',
                                                                boxShadow: '0 2px 5px rgba(0,0,0,0.18)'
                                                            }
                                                        }}
                                                    >
                                                        <DeleteOutlineIcon sx={{ fontSize: 9, color: '#dc2626' }} />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Box>
                                    )}

                                    {/* Resize Handle - only on block end */}
                                    {currentSlot && isBlockEnd && (
                                        <div
                                            className="resize-handle"
                                            onMouseDown={(e) => {
                                                e.stopPropagation();
                                                onResizeStart(currentSlot.playlistId);
                                            }}
                                        >
                                            <div className="resize-handle-icon">
                                                <span>=</span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </React.Fragment>
                ))}
            </div>
        </Paper>
    );
};

export default TimeGrid;
